/**
 * Shopify Storefront API GraphQL Queries
 */

// Fragment for consistent image data
const IMAGE_FRAGMENT = `
  fragment ImageFields on Image {
    url
    altText
    width
    height
  }
`;

// Fragment for money fields
const MONEY_FRAGMENT = `
  fragment MoneyFields on MoneyV2 {
    amount
    currencyCode
  }
`;

// Fragment for product variant
const VARIANT_FRAGMENT = `
  fragment VariantFields on ProductVariant {
    id
    title
    availableForSale
    sku
    barcode
    price {
      ...MoneyFields
    }
    compareAtPrice {
      ...MoneyFields
    }
    selectedOptions {
      name
      value
    }
    image {
      ...ImageFields
    }
  }
`;

// Fragment for product card (minimal data for listings)
const PRODUCT_CARD_FRAGMENT = `
  fragment ProductCardFields on Product {
    id
    handle
    title
    vendor
    productType
    availableForSale
    featuredImage {
      ...ImageFields
    }
    priceRange {
      minVariantPrice {
        ...MoneyFields
      }
      maxVariantPrice {
        ...MoneyFields
      }
    }
    compareAtPriceRange {
      minVariantPrice {
        ...MoneyFields
      }
      maxVariantPrice {
        ...MoneyFields
      }
    }
    options {
      id
      name
      values
    }
    variants(first: 10) {
      edges {
        node {
          ...VariantFields
        }
      }
    }
    tags
  }
`;

// Full product fragment for product pages
const PRODUCT_FULL_FRAGMENT = `
  fragment ProductFullFields on Product {
    id
    handle
    title
    description
    descriptionHtml
    vendor
    productType
    tags
    availableForSale
    seo {
      title
      description
    }
    featuredImage {
      ...ImageFields
    }
    images(first: 10) {
      edges {
        node {
          ...ImageFields
        }
      }
    }
    options {
      id
      name
      values
    }
    variants(first: 50) {
      edges {
        node {
          ...VariantFields
        }
      }
    }
    priceRange {
      minVariantPrice {
        ...MoneyFields
      }
      maxVariantPrice {
        ...MoneyFields
      }
    }
    compareAtPriceRange {
      minVariantPrice {
        ...MoneyFields
      }
      maxVariantPrice {
        ...MoneyFields
      }
    }
    metafields(identifiers: [
      { namespace: "custom", key: "features" },
      { namespace: "custom", key: "specifications" },
      { namespace: "custom", key: "compatibility" },
      { namespace: "custom", key: "warranty" },
      { namespace: "mm-google-shopping", key: "condition" },
      { namespace: "custom", key: "faqs" }
    ]) {
      key
      namespace
      value
      type
    }
  }
`;

// Cart line fragment
const CART_LINE_FRAGMENT = `
  fragment CartLineFields on CartLine {
    id
    quantity
    merchandise {
      ... on ProductVariant {
        id
        title
        product {
          id
          handle
          title
          featuredImage {
            ...ImageFields
          }
          vendor
        }
        price {
          ...MoneyFields
        }
        selectedOptions {
          name
          value
        }
      }
    }
    cost {
      totalAmount {
        ...MoneyFields
      }
      compareAtAmountPerQuantity {
        ...MoneyFields
      }
    }
  }
`;

// Cart fragment
const CART_FRAGMENT = `
  fragment CartFields on Cart {
    id
    checkoutUrl
    totalQuantity
    cost {
      subtotalAmount {
        ...MoneyFields
      }
      totalAmount {
        ...MoneyFields
      }
      totalTaxAmount {
        ...MoneyFields
      }
    }
    lines(first: 100) {
      edges {
        node {
          ...CartLineFields
        }
      }
    }
  }
`;

// Queries
export const GET_SHOP_INFO = `
  query GetShopInfo {
    shop {
      name
      description
      primaryDomain {
        url
      }
      brand {
        logo {
          image {
            url
            altText
            width
            height
          }
        }
        slogan
        shortDescription
      }
    }
  }
`;

export const GET_PRODUCTS = `
  ${MONEY_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${VARIANT_FRAGMENT}
  ${PRODUCT_CARD_FRAGMENT}
  query GetProducts($first: Int!, $after: String, $sortKey: ProductSortKeys, $reverse: Boolean, $query: String) {
    products(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse, query: $query) {
      edges {
        node {
          ...ProductCardFields
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

export const GET_PRODUCT_BY_HANDLE = `
  ${MONEY_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${VARIANT_FRAGMENT}
  ${PRODUCT_FULL_FRAGMENT}
  query GetProductByHandle($handle: String!) {
    product(handle: $handle) {
      ...ProductFullFields
    }
  }
`;

export const GET_PRODUCT_RECOMMENDATIONS = `
  ${MONEY_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${VARIANT_FRAGMENT}
  ${PRODUCT_CARD_FRAGMENT}
  query GetProductRecommendations($productId: ID!) {
    productRecommendations(productId: $productId) {
      ...ProductCardFields
    }
  }
`;

export const GET_COLLECTIONS = `
  ${IMAGE_FRAGMENT}
  query GetCollections($first: Int!) {
    collections(first: $first) {
      edges {
        node {
          id
          handle
          title
          description
          image {
            ...ImageFields
          }
        }
      }
    }
  }
`;

export const GET_COLLECTION_BY_HANDLE = `
  ${MONEY_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${VARIANT_FRAGMENT}
  ${PRODUCT_CARD_FRAGMENT}
  query GetCollectionByHandle(
    $handle: String!
    $first: Int!
    $after: String
    $sortKey: ProductCollectionSortKeys
    $reverse: Boolean
    $filters: [ProductFilter!]
  ) {
    collection(handle: $handle) {
      id
      handle
      title
      description
      descriptionHtml
      seo {
        title
        description
      }
      image {
        ...ImageFields
      }
      products(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse, filters: $filters) {
        filters {
          id
          label
          type
          values {
            id
            label
            count
            input
          }
        }
        edges {
          node {
            ...ProductCardFields
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

export const SEARCH_PRODUCTS = `
  ${MONEY_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${VARIANT_FRAGMENT}
  ${PRODUCT_CARD_FRAGMENT}
  query SearchProducts($query: String!, $first: Int!, $after: String) {
    search(query: $query, first: $first, after: $after, types: [PRODUCT]) {
      edges {
        node {
          ... on Product {
            ...ProductCardFields
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

// Cart Mutations
export const CREATE_CART = `
  ${MONEY_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${CART_LINE_FRAGMENT}
  ${CART_FRAGMENT}
  mutation CreateCart($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        ...CartFields
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const ADD_TO_CART = `
  ${MONEY_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${CART_LINE_FRAGMENT}
  ${CART_FRAGMENT}
  mutation AddToCart($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFields
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const UPDATE_CART_LINE = `
  ${MONEY_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${CART_LINE_FRAGMENT}
  ${CART_FRAGMENT}
  mutation UpdateCartLine($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFields
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const REMOVE_FROM_CART = `
  ${MONEY_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${CART_LINE_FRAGMENT}
  ${CART_FRAGMENT}
  mutation RemoveFromCart($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        ...CartFields
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const GET_CART = `
  ${MONEY_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${CART_LINE_FRAGMENT}
  ${CART_FRAGMENT}
  query GetCart($cartId: ID!) {
    cart(id: $cartId) {
      ...CartFields
    }
  }
`;

// Predictive search for autocomplete
export const PREDICTIVE_SEARCH = `
  ${IMAGE_FRAGMENT}
  query PredictiveSearch($query: String!, $limit: Int!) {
    predictiveSearch(query: $query, limit: $limit, types: [PRODUCT, COLLECTION]) {
      products {
        id
        handle
        title
        vendor
        featuredImage {
          ...ImageFields
        }
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
        }
      }
      collections {
        id
        handle
        title
        image {
          ...ImageFields
        }
      }
    }
  }
`;

// Get page by handle with metafields (for CMS-managed content)
export const GET_PAGE_BY_HANDLE = `
  query GetPageByHandle($handle: String!) {
    page(handle: $handle) {
      id
      handle
      title
      body
      bodySummary
      seo {
        title
        description
      }
      createdAt
      updatedAt
      metafields(identifiers: [
        { namespace: "custom", key: "hero_title" },
        { namespace: "custom", key: "hero_subtitle" },
        { namespace: "custom", key: "hero_badge" },
        { namespace: "custom", key: "hero_image" },
        { namespace: "custom", key: "sections" },
        { namespace: "custom", key: "stats" },
        { namespace: "custom", key: "faqs" },
        { namespace: "custom", key: "cta_title" },
        { namespace: "custom", key: "cta_subtitle" },
        { namespace: "custom", key: "cta_button_text" },
        { namespace: "custom", key: "cta_button_link" }
      ]) {
        key
        namespace
        value
        type
      }
    }
  }
`;

// Get shop metafields for global settings
export const GET_SHOP_METAFIELDS = `
  query GetShopMetafields {
    shop {
      name
      description
      brand {
        shortDescription
        slogan
      }
      metafields(identifiers: [
        { namespace: "custom", key: "phone" },
        { namespace: "custom", key: "email" },
        { namespace: "custom", key: "address" },
        { namespace: "custom", key: "business_hours" },
        { namespace: "custom", key: "social_links" },
        { namespace: "custom", key: "announcement_text" },
        { namespace: "custom", key: "free_shipping_threshold" }
      ]) {
        key
        namespace
        value
        type
      }
    }
  }
`;

// Get all pages
export const GET_PAGES = `
  query GetPages($first: Int!) {
    pages(first: $first) {
      edges {
        node {
          id
          handle
          title
          bodySummary
          createdAt
          updatedAt
        }
      }
    }
  }
`;

// Get blog articles
export const GET_BLOG_ARTICLES = `
  ${IMAGE_FRAGMENT}
  query GetBlogArticles($blogHandle: String!, $first: Int!) {
    blog(handle: $blogHandle) {
      id
      title
      articles(first: $first, sortKey: PUBLISHED_AT, reverse: true) {
        edges {
          node {
            id
            handle
            title
            excerpt
            contentHtml
            publishedAt
            authorV2 {
              name
            }
            image {
              ...ImageFields
            }
            tags
          }
        }
      }
    }
  }
`;

// Get single blog article
export const GET_BLOG_ARTICLE = `
  ${IMAGE_FRAGMENT}
  query GetBlogArticle($blogHandle: String!, $articleHandle: String!) {
    blog(handle: $blogHandle) {
      articleByHandle(handle: $articleHandle) {
        id
        handle
        title
        excerpt
        contentHtml
        publishedAt
        authorV2 {
          name
        }
        image {
          ...ImageFields
        }
        tags
        seo {
          title
          description
        }
      }
    }
  }
`;

// Get collections with product count (for navigation)
export const GET_COLLECTIONS_WITH_PRODUCT_COUNT = `
  ${IMAGE_FRAGMENT}
  query GetCollectionsWithProductCount($first: Int!) {
    collections(first: $first) {
      edges {
        node {
          id
          handle
          title
          description
          image {
            ...ImageFields
          }
          products(first: 1) {
            edges {
              node {
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const CUSTOMER_CREATE = `
  mutation customerCreate($input: CustomerCreateInput!) {
    customerCreate(input: $input) {
      customer {
        id
        email
        acceptsMarketing
      }
      customerUserErrors {
        code
        field
        message
      }
    }
  }
`;
